import mitt from 'mitt'

import {
  DEFAULT_KNOWLEDGE_BASE_SOURCE,
  KNOWLEDGE_BASE_BASE_URL,
  KNOWLEDGE_BASE_CATEGORY_TONES,
  KNOWLEDGE_BASE_DEFAULT_ICON,
  KNOWLEDGE_BASE_ICONS,
  KNOWLEDGE_BASE_PUBLIC_SITE_URL,
  KNOWLEDGE_BASE_SOURCES,
  type KnowledgeBaseCatalogue,
  type KnowledgeBaseDocument,
  type KnowledgeBaseSource,
} from '@/lib/definitions/knowledgeBase'

export const KNOWLEDGE_BASE_HOME = ''

export interface KnowledgeBaseTarget {
  path: string
  anchor?: string
}

type KnowledgeBaseEvents = {
  open: { key?: string }
  close: undefined
  toggle: undefined
}

export const knowledgeBaseEvents = mitt<KnowledgeBaseEvents>()

export const openKnowledgeBase = (key?: string): void =>
  knowledgeBaseEvents.emit('open', { key })

export const closeKnowledgeBase = (): void =>
  knowledgeBaseEvents.emit('close', undefined)

export const toggleKnowledgeBase = (): void =>
  knowledgeBaseEvents.emit('toggle', undefined)

const isSource = (value: string): value is KnowledgeBaseSource =>
  value in KNOWLEDGE_BASE_SOURCES

export const parseKnowledgeBaseKey = (key: string): KnowledgeBaseTarget => {
  const hashIndex = key.indexOf('#')
  const rawPath = hashIndex === -1 ? key : key.slice(0, hashIndex)
  const anchor = hashIndex === -1 ? undefined : key.slice(hashIndex + 1)
  const path = rawPath
    .trim()
    .replace(/^\/+/, '')
    .replace(/^kb\//, '')
    .replace(/\.md$/, '')
  const [root] = path.split('/')
  const fullPath =
    path.includes('/') && isSource(root)
      ? path
      : `${DEFAULT_KNOWLEDGE_BASE_SOURCE}/${path}`
  return { path: fullPath, anchor: anchor || undefined }
}

export const formatKnowledgeBaseKey = ({
  path,
  anchor,
}: KnowledgeBaseTarget): string => (anchor ? `${path}#${anchor}` : path)

export const knowledgeBaseDocumentUrl = (path: string): string =>
  `${KNOWLEDGE_BASE_BASE_URL}/${path}.md`

export const knowledgeBaseCatalogueUrl = (): string =>
  `${KNOWLEDGE_BASE_BASE_URL}/catalogue.json`

export const knowledgeBasePublicSiteUrl = (path: string): string => {
  const [source, ...rest] = path.split('/')
  return source === 'public' && rest.length > 0
    ? `${KNOWLEDGE_BASE_PUBLIC_SITE_URL}/${rest.join('/')}/`
    : `${KNOWLEDGE_BASE_PUBLIC_SITE_URL}/`
}

export const knowledgeBasePdfUrl = (path: string): string =>
  `${KNOWLEDGE_BASE_BASE_URL}/pdf/${path}.pdf`

export const knowledgeBaseSourceOf = (path: string): KnowledgeBaseSource => {
  const [root] = path.split('/')
  return isSource(root) ? root : DEFAULT_KNOWLEDGE_BASE_SOURCE
}

export const findKnowledgeBaseDocument = (
  documents: KnowledgeBaseDocument[],
  path: string
): KnowledgeBaseDocument | undefined =>
  documents.find((document) => document.path === path)

const directoryOf = (path: string) =>
  path.includes('/') ? path.slice(0, path.lastIndexOf('/') + 1) : ''

export const resolveKnowledgeBaseHref = (
  fromPath: string,
  href: string,
  documents: KnowledgeBaseDocument[]
): string => {
  const hashIndex = href.indexOf('#')
  const target = hashIndex === -1 ? href : href.slice(0, hashIndex)
  const anchor = hashIndex === -1 ? undefined : href.slice(hashIndex + 1)
  const segments: string[] = []
  for (const part of `${directoryOf(fromPath)}${target.replace(/\.md$/, '')}`.split(
    '/'
  )) {
    if (part === '..') segments.pop()
    else if (part && part !== '.') segments.push(part)
  }
  let path = segments.join('/')
  const sibling = `portal/public_${segments.at(-1)}`
  if (
    !findKnowledgeBaseDocument(documents, path) &&
    findKnowledgeBaseDocument(documents, sibling)
  ) {
    path = sibling
  }
  return formatKnowledgeBaseKey({ path, anchor })
}

export const buildKnowledgeBaseCatalogue = (
  catalogue: KnowledgeBaseCatalogue | undefined
): KnowledgeBaseDocument[] =>
  (catalogue?.sections ?? []).flatMap((section, position) =>
    section.articles
      .filter((article) => isSource(article.path.split('/')[0]))
      .map((article) => ({
        path: article.path,
        title: article.title,
        description: article.description,
        category: {
          label: section.title,
          tone: KNOWLEDGE_BASE_CATEGORY_TONES[
            position % KNOWLEDGE_BASE_CATEGORY_TONES.length
          ],
        },
        icon:
          KNOWLEDGE_BASE_ICONS[
            article.path
              .split('/')
              .at(-1)
              ?.replace(/^public_/, '') ?? ''
          ] ?? KNOWLEDGE_BASE_DEFAULT_ICON,
      }))
  )

export const resolveKnowledgeBaseAsset = (
  fromPath: string,
  src: string
): string =>
  /^(https?:)?\/\//.test(src) || src.startsWith('/')
    ? src
    : `${KNOWLEDGE_BASE_BASE_URL}/${directoryOf(fromPath)}${src.replace(/^\.\//, '')}`

const PUBLIC_SITE_HOST = /^(www\.)?inter\.link$/i
const PUBLIC_SITE_ROOT = 'knowledgebase'
const PUBLIC_SITE_ALIASES: Record<string, string> = {
  'knowledgebase-flextunnel': 'flextunnel',
}

export const knowledgeBaseKeyFromUrl = (
  url: string,
  documents: KnowledgeBaseDocument[]
): string | null => {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  const [root, page] = parsed.pathname.split('/').filter(Boolean)
  if (!PUBLIC_SITE_HOST.test(parsed.hostname) || root !== PUBLIC_SITE_ROOT) {
    return null
  }
  if (!page) return KNOWLEDGE_BASE_HOME
  const slug = PUBLIC_SITE_ALIASES[page] ?? page
  const path = [`public/${slug}`, `portal/public_${slug}`].find((candidate) =>
    findKnowledgeBaseDocument(documents, candidate)
  )
  if (!path) return null
  return formatKnowledgeBaseKey({
    path,
    anchor: parsed.hash.slice(1) || undefined,
  })
}
